import { useEffect, useState } from "react";
import { ActionsButton } from "@/components/ActionsButton";
import { toast } from "sonner";
import { Building2, Ban, CheckCircle2, Search, CalendarPlus, Infinity as InfinityIcon, Mail, Save } from "lucide-react";
import api from "@/lib/api";
import { PlatformWhatsApp } from "@/components/whatsapp/PlatformWhatsApp";
import { UpgradePaymentCard, SubscriptionPaymentCard } from "@/components/company/UpgradePaymentCard";
import { ArchiveCard } from "@/components/company/ArchiveCard";
import { fmtDate, formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-xl border border-border bg-card p-5">
    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
    <div className="font-heading text-3xl font-extrabold mt-1" data-testid={testid}>{value}</div>
  </div>
);

const PlanBadge = ({ c }) => {
  if (c.plan !== "trial") return <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100" data-testid={`company-plan-${c.id}`}>Completo</Badge>;
  if (c.trial_expired) return <Badge className="bg-red-100 text-red-700 hover:bg-red-100" data-testid={`company-plan-${c.id}`}>Prova scaduta</Badge>;
  return (
    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`company-plan-${c.id}`} title={`Scade il ${fmtDate(c.trial_ends_at)}`}>
      Prova · {c.trial_days_left} gg
    </Badge>
  );
};

const RowActions = ({ c, onPlan, onToggle }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <ActionsButton data-testid={`company-actions-${c.id}`} />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuItem onClick={() => onPlan(c, { extend_days: 7 })} data-testid={`company-extend7-${c.id}`}><CalendarPlus className="w-4 h-4 mr-2" /> +7 giorni di prova</DropdownMenuItem>
      <DropdownMenuItem onClick={() => onPlan(c, { extend_days: 30 })} data-testid={`company-extend30-${c.id}`}><CalendarPlus className="w-4 h-4 mr-2" /> +30 giorni di prova</DropdownMenuItem>
      {c.plan === "trial" && (
        <DropdownMenuItem onClick={() => onPlan(c, { plan: "full" })} data-testid={`company-full-${c.id}`}><InfinityIcon className="w-4 h-4 mr-2" /> Attiva senza scadenza</DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => onToggle(c)} className={c.status === "suspended" ? "" : "text-destructive focus:text-destructive"} data-testid={`company-toggle-${c.id}`}>
        {c.status === "suspended" ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Riattiva</> : <><Ban className="w-4 h-4 mr-2" /> Sospendi</>}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const CompanyRow = ({ c, onPlan, onToggle }) => {
  const suspended = c.status === "suspended";
  return (
    <tr className="border-t border-border hover:bg-secondary/40 transition-colors" data-testid={`company-row-${c.id}`}>
      <td className="py-3 px-4">
        <div className="font-semibold">{c.ragione_sociale} {c.is_mine && <Badge variant="outline" className="ml-1 text-[10px]">La tua</Badge>}</div>
        <div className="text-xs text-muted-foreground">{c.piva}</div>
      </td>
      <td className="py-3 px-4 text-sm"><div>{c.owner_name}</div><div className="text-xs text-muted-foreground">{c.owner_email}</div></td>
      <td className="py-3 px-4 text-sm text-muted-foreground whitespace-nowrap">{fmtDate(c.created_at)}</td>
      <td className="py-3 px-4 text-sm tabular-nums">{c.users_count} · {c.contacts_count} · {c.quotes_count}</td>
      <td className="py-3 px-4"><PlanBadge c={c} /></td>
      <td className="py-3 px-4">
        <Badge className={suspended ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"} data-testid={`company-status-${c.id}`}>
          {suspended ? "Sospesa" : "Operativa"}
        </Badge>
      </td>
      <td className="py-3 px-4 text-right">{!c.is_mine && <RowActions c={c} onPlan={onPlan} onToggle={onToggle} />}</td>
    </tr>
  );
};

const PlatformCard = () => {
  const [form, setForm] = useState({ support_email: "", support_whatsapp: "" });
  useEffect(() => { api.get("/superadmin/platform").then((r) => setForm({ support_email: r.data.support_email || "", support_whatsapp: r.data.support_whatsapp || "" })).catch(() => {}); }, []);
  const save = async () => {
    try {
      await api.put("/superadmin/platform", form);
      window.dispatchEvent(new Event("platform-updated"));
      toast.success("Contatti assistenza salvati");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="platform-card">
      <div>
        <h3 className="font-heading font-bold flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> Contatti assistenza K Gestionale</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Mostrati alle ditte in prova, nella schermata «Prova scaduta» e nelle pagine Privacy e Termini.</p>
      </div>
      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div className="space-y-1.5"><Label>Email assistenza</Label><Input value={form.support_email} onChange={(e) => setForm({ ...form, support_email: e.target.value })} data-testid="platform-email-input" /></div>
        <div className="space-y-1.5"><Label>WhatsApp (facoltativo)</Label><Input value={form.support_whatsapp} onChange={(e) => setForm({ ...form, support_whatsapp: e.target.value })} placeholder="+39 …" data-testid="platform-whatsapp-input" /></div>
        <Button onClick={save} data-testid="platform-save-button"><Save className="w-4 h-4 mr-1.5" /> Salva</Button>
      </div>
    </div>
  );
};

export default function Aziende() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);

  const load = () => api.get("/superadmin/companies").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const confirm = async () => {
    const status = target.status === "suspended" ? "active" : "suspended";
    try {
      await api.patch(`/superadmin/companies/${target.id}/status`, { status });
      toast.success(status === "suspended" ? "Azienda sospesa" : "Azienda riattivata");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setTarget(null);
  };

  const changePlan = async (c, body) => {
    try {
      const r = await api.patch(`/superadmin/companies/${c.id}/plan`, body);
      toast.success(r.data.plan === "full" ? `${c.ragione_sociale}: attivata senza scadenza` : `${c.ragione_sociale}: prova fino al ${fmtDate(r.data.trial_ends_at)}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = items.filter((c) => `${c.ragione_sociale} ${c.piva} ${c.owner_email}`.toLowerCase().includes(q.toLowerCase()));
  const suspendedCount = items.filter((c) => c.status === "suspended").length;
  const trialCount = items.filter((c) => c.plan === "trial" && !c.trial_expired).length;

  return (
    <div className="space-y-6 animate-in-up" data-testid="aziende-page">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Aziende registrate" value={items.length} testid="kpi-companies-total" />
        <Kpi label="Attive" value={items.length - suspendedCount} testid="kpi-companies-active" />
        <Kpi label="In prova" value={trialCount} testid="kpi-companies-trial" />
        <Kpi label="Sospese" value={suspendedCount} testid="kpi-companies-suspended" />
      </div>
      <PlatformCard />
      <UpgradePaymentCard />
      <SubscriptionPaymentCard />
      <ArchiveCard />
      <PlatformWhatsApp />
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, P.IVA, email…" className="pl-9" data-testid="companies-search-input" />
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-left min-w-[860px]">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="py-3 px-4">Azienda</th><th className="py-3 px-4">Titolare</th><th className="py-3 px-4">Registrata</th>
              <th className="py-3 px-4">Utenti · Contatti · Preventivi</th><th className="py-3 px-4">Piano</th><th className="py-3 px-4">Stato</th><th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => <CompanyRow key={c.id} c={c} onPlan={changePlan} onToggle={setTarget} />)}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-sm text-muted-foreground"><Building2 className="w-6 h-6 mx-auto mb-2" />Nessuna azienda trovata</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent data-testid="company-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{target?.status === "suspended" ? "Riattivare" : "Sospendere"} {target?.ragione_sociale}?</AlertDialogTitle>
            <AlertDialogDescription>
              {target?.status === "suspended"
                ? "Gli utenti dell'azienda potranno di nuovo accedere ai propri dati."
                : "Tutti gli utenti dell'azienda verranno disconnessi e non potranno accedere finché non la riattivi. I dati restano salvati."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="company-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} data-testid="company-confirm-ok">Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
